import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './auth/AuthContext';
import AppShell from './components/layout/AppShell';
import HomePage from './pages/home';
import LoginPage from './pages/login';
import SetPasswordPage from './pages/login/SetPasswordPage';
import InviteGate from './auth/InviteGate';
import TripsPage from './pages/trips';
import PlanBoardPage from './pages/plan/PlanBoardPage';
import NewTripPage from './pages/plan/NewTripPage';
import PastePage from './pages/plan/PastePage';
import TripEditPage from './pages/trips/TripEditPage';
import StopDetailPage from './pages/stops/StopDetailPage';
import StopEditPage from './pages/stops/StopEditPage';
import SharePage from './pages/share/SharePage';
import ProfilePage from './pages/profile/ProfilePage';
import ConfigError from './components/ConfigError';
import { isConfigured, missingConfig } from './supabaseClient';

function Spinner() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );
}


/** The /reset-password route: same screen, but navigate on once it's done. */
function SetPasswordRoute() {
  const navigate = useNavigate();
  return <SetPasswordPage onComplete={() => navigate('/trips', { replace: true })} />;
}

function AuthedRoute({ children, wide }: { children: React.ReactNode; wide?: boolean }) {
  const { session, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!session) return <Navigate to="/login" replace />;
  return <AppShell wide={wide}>{children}</AppShell>;
}

export default function App() {
  // Checked before anything else renders. Every route — reader and author alike
  // — needs Supabase, so a missing build-time value has exactly one honest
  // outcome: say so, visibly, instead of rendering nothing.
  if (!isConfigured) return <ConfigError missing={missingConfig} />;

  return (
    <AuthProvider>
      <BrowserRouter>
        <InviteGate>
        <Routes>
          {/* Public front door. No auth — this is the face of roamoir.me. */}
          <Route path="/" element={<HomePage />} />

          {/* Public, no-auth share viewer */}
          <Route path="/share/:shareId" element={<SharePage />} />

          {/* Auth gate */}
          <Route path="/login" element={<LoginPage />} />
          {/* Used when Supabase honours redirectTo. InviteGate covers the case
              where it does not and the link lands somewhere else entirely. */}
          <Route path="/reset-password" element={<SetPasswordRoute />} />

          {/* Owner-only routes */}
          <Route path="/trips" element={<AuthedRoute wide><TripsPage /></AuthedRoute>} />
          <Route path="/trips/new" element={<AuthedRoute wide><NewTripPage /></AuthedRoute>} />
          {/* The planning board is the trip page. It needs the full window width. */}
          <Route path="/trips/:tripId" element={<AuthedRoute wide><PlanBoardPage /></AuthedRoute>} />
          <Route path="/trips/:tripId/paste" element={<AuthedRoute wide><PastePage /></AuthedRoute>} />
          <Route path="/trips/:tripId/edit" element={<AuthedRoute><TripEditPage /></AuthedRoute>} />
          <Route path="/stops/:stopId" element={<AuthedRoute><StopDetailPage /></AuthedRoute>} />
          <Route path="/stops/:stopId/edit" element={<AuthedRoute><StopEditPage /></AuthedRoute>} />
          <Route path="/profile" element={<AuthedRoute><ProfilePage /></AuthedRoute>} />

          {/* Anything unknown falls back to the front door rather than a
              protected route, which would bounce an anonymous visitor to
              /login for a simple typo. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </InviteGate>
      </BrowserRouter>
    </AuthProvider>
  );
}
